import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';

import { COUNTRY_CODE_TO_NAMES } from '../constants';
import { replaceNestedWithId } from '../utils/replace-nested-with-id';
import { isDraft } from './utils';

export const groupLinesBy = (lines, key, path, additionalProperties = []) => {
  const combinedLines = lines.reduce((groups, line) => {
    const value = get(line, path);
    const additionalProps = {};

    additionalProperties.forEach(prop => {
      additionalProps[prop.key] = get(line, prop.path);
    });

    const currentGroup = groups.find(group => group[key] === value);

    if (currentGroup) {
      currentGroup.lines.push(line);
    } else {
      groups.push({
        [key]: value,
        lines: [line],
        ...additionalProps,
      });
    }

    return groups;
  }, []);

  return combinedLines;
};

export function getProductLabel(product) {
  return `${product.name} - ${product.productGroup}`;
}

function generateDefaults(line) {
  const { countryCode } = line;
  return {
    ...line,
    product: {
      ...line.product,
      label: getProductLabel(line.product),
    },
    country: countryCode
      ? { name: COUNTRY_CODE_TO_NAMES[countryCode], code: countryCode }
      : { name: 'All' },
  };
}

export function transformCampaignLine(line, products) {
  const lineWithProduct = {
    ...line,
    selected: false,
    adjustedFee: line.adjustedFee * 100,
    originalFee: line.originalFee * 100,
    product: products.find(p => p.id === line.product.id) || line.product,
  };
  return generateDefaults(lineWithProduct);
}

export const combineLinesByBusinesses = (lines, expanded = false) => {
  return groupLinesBy(lines, 'label', 'product.business').map(business => ({
    label: business.label,
    expanded,
    products: groupLinesBy(business.lines, 'id', 'product.id', [
      { key: 'label', path: 'product.label' },
    ]).map(product => ({
      label: product.label,
      id: product.id,
      expanded,
      countries: groupLinesBy(product.lines, 'label', 'country.name').map(
        country => ({
          label: country.label,
          lines: country.lines.map(({ id }) => id),
        }),
      ),
    })),
  }));
};

export function getBlankProductMeta(productId) {
  return {
    productId,
    creativeCollabaryId: '',
    creativeCampaignSetup: 0,
    creativeConcept: 0,
    creativeHandlingFee: 0,
    creativeTalent: 0,
    creativeDesign: 0,
    creativeReach: 0,
    creativeKsk: 0,
    costPerShootingDay: 0,
    additionalModelCost: 0,
    videoUpgrade: 0,
    locationUpgrade: 0,
    advancedUsageRights: 0,
    numberOfShootingDays: 0,
    landingPageCost: 0,
    mastercopyCost: 0,
    numberOfURLs: 0,
    collectionIds: [],
    useCollectionIds: false,
    contentIds: [],
    useContentIds: false,
  };
}

export function shouldHaveProductMeta(product) {
  return (
    product.supportsCreativeProductionBudgetBreakdown ||
    product.supportsCreativeEnhancedProductShootingBudgetBreakdown ||
    product.supportsCreativeLandingPageBudgetBreakdown ||
    product.supportsNumberOfURLs ||
    product.supportsCollectionIds ||
    product.supportsContentIds
  );
}

export function transformProductMetasForRendering(campaign, products) {
  const uniqueProductsInCampaign = uniq(
    campaign.campaignLines.map(line =>
      products.find(p => p.id === line.product.id),
    ),
  );
  return uniqueProductsInCampaign
    .map(p => {
      const productMeta = campaign.productMetas.find(
        pm => pm.productId === p.id,
      );
      if (productMeta) {
        return {
          ...productMeta,
          useCollectionIds: productMeta.collectionIds.length > 0,
          useContentIds: productMeta.contentIds.length > 0,
        };
      }
      if (shouldHaveProductMeta(p)) {
        return getBlankProductMeta(p.id);
      }
      return null;
    })
    .filter(n => n);
}

export function transformMerchantIdsForRendering(campaign) {
  return campaign.merchants && campaign.merchants.map(item => item.id);
}

export default function transformForRendering(campaign, products = []) {
  const { campaignLines = [] } = campaign;

  const transformedCampaignLines = campaignLines.map(line =>
    transformCampaignLine(line, products),
  );

  const campaignLinesById = keyBy(transformedCampaignLines, 'id');
  const transformedCampaign = replaceNestedWithId(campaign, 'owner');

  if (transformedCampaignLines.length === 0) {
    return { campaign: transformedCampaign, campaignLinesById: {} };
  }

  const nonDraftLines = transformedCampaignLines.filter(line => !isDraft(line));
  const combinedBusinesses = combineLinesByBusinesses(nonDraftLines);

  transformedCampaign.brandIds = campaign.brands.map(brand => brand.id);
  transformedCampaign.productMetas = transformProductMetasForRendering(
    transformedCampaign,
    products,
  );
  transformedCampaign.campaignLines = combinedBusinesses;
  transformedCampaign.campaignLineIds = transformedCampaignLines.map(
    cl => cl.id,
  );

  transformedCampaign.merchantIds = transformMerchantIdsForRendering(campaign);

  return {
    campaign: transformedCampaign,
    campaignLinesById,
  };
}
